import React, { useState, useMemo } from 'react';
import { 
  Database, Search, Filter, BarChart3, Thermometer, Zap, 
  Droplets, DollarSign, Leaf, AlertTriangle, CheckCircle, 
  XCircle, Info, Download, Layers, TestTube, Eye, Shield,
  Lightbulb, Package, ChevronDown, ChevronUp, Star, TrendingUp
} from 'lucide-react';
import { 
  MATERIALS_DATABASE,
  MaterialsAnalyzer,
  type Material
} from '../../utils/materialsDatabase';

const MaterialsDatabaseComponent: React.FC = () => {
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'table' | 'compare'>('grid');
  const [expandedMaterial, setExpandedMaterial] = useState<string | null>(null);
  
  // AI Recommendation state
  const [aiRequirements, setAiRequirements] = useState({
    minTensileStrength: 0,
    minImpactStrength: 0,
    maxPrice: 100,
    minServiceTemp: -50,
    maxServiceTemp: 100,
    needsTransparency: false,
    chemicalExposure: [] as string[],
    outdoor: false,
    foodContact: false
  });
  const [showAiRecommendations, setShowAiRecommendations] = useState(false);

  // Filtrowanie materiałów
  const filteredMaterials = useMemo(() => {
    let materials = [...MATERIALS_DATABASE];
    
    // Filtr kategorii
    if (categoryFilter !== 'all') {
      materials = materials.filter(m => m.category === categoryFilter);
    }
    
    // Wyszukiwanie
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      materials = materials.filter(m => 
        m.name.toLowerCase().includes(term) ||
        m.tradenames.some(t => t.toLowerCase().includes(term)) ||
        m.applications.some(a => a.toLowerCase().includes(term))
      );
    }
    
    return materials;
  }, [searchTerm, categoryFilter]);

  // AI Recommendations
  const aiRecommendedMaterials = useMemo(() => {
    if (!showAiRecommendations) return [];
    
    return MaterialsAnalyzer.recommendMaterial({
      minTensileStrength: aiRequirements.minTensileStrength || undefined,
      minImpactStrength: aiRequirements.minImpactStrength || undefined,
      maxPrice: aiRequirements.maxPrice || undefined,
      minServiceTemp: aiRequirements.minServiceTemp || undefined,
      maxServiceTemp: aiRequirements.maxServiceTemp || undefined,
      needsTransparency: aiRequirements.needsTransparency,
      chemicalExposure: aiRequirements.chemicalExposure.length > 0 
        ? aiRequirements.chemicalExposure as any 
        : undefined,
      outdoor: aiRequirements.outdoor,
      foodContact: aiRequirements.foodContact
    });
  }, [aiRequirements, showAiRecommendations]);

  const toggleMaterialSelection = (materialId: string) => {
    setSelectedMaterials(prev => {
      if (prev.includes(materialId)) {
        return prev.filter(id => id !== materialId);
      }
      if (prev.length >= 4) {
        alert('Możesz porównać maksymalnie 4 materiały jednocześnie');
        return prev;
      }
      return [...prev, materialId];
    });
  };

  const getPropertyColor = (value: number, min: number, max: number): string => {
    const ratio = (value - min) / (max - min);
    if (ratio < 0.33) return 'text-red-400';
    if (ratio < 0.67) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getResistanceColor = (level: string): string => {
    switch (level) {
      case 'excellent': return 'bg-green-900/30 text-green-400 border border-green-700';
      case 'good': return 'bg-blue-900/30 text-blue-400 border border-blue-700';
      case 'fair': return 'bg-yellow-900/30 text-yellow-400 border border-yellow-700';
      case 'poor': return 'bg-red-900/30 text-red-400 border border-red-700';
      default: return 'bg-gray-800 text-gray-400 border border-gray-700';
    }
  };

  const downloadComparisonReport = () => {
    if (selectedMaterials.length === 0) {
      alert('Wybierz materiały do porównania');
      return;
    }
    
    const report = MaterialsAnalyzer.generateComparisonReport(selectedMaterials);
    const blob = new Blob([report], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'materials-comparison.md';
    a.click();
    URL.revokeObjectURL(url);
  };

  const MaterialCard: React.FC<{ material: Material }> = ({ material }) => {
    const isExpanded = expandedMaterial === material.id;
    const isSelected = selectedMaterials.includes(material.id);
    const performanceIndex = MaterialsAnalyzer.calculatePerformanceIndex(material.id);
    
    return (
      <div className={`bg-zinc-800 rounded-lg shadow-lg hover:shadow-xl transition-all border ${
        isSelected ? 'border-blue-500 ring-2 ring-blue-500/50' : 'border-zinc-700'
      }`}>
        <div className="p-4">
          {/* Header */}
          <div className="flex justify-between items-start mb-3">
            <div>
              <h3 className="text-lg font-bold text-white">{material.name}</h3>
              <p className="text-sm text-gray-400">{material.tradenames[0]}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => toggleMaterialSelection(material.id)}
                className={`p-2 rounded-lg transition-all ${
                  isSelected 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-zinc-700 hover:bg-zinc-600 text-gray-300'
                }`}
                title={isSelected ? 'Usuń z porównania' : 'Dodaj do porównania'}
              >
                {isSelected ? <CheckCircle className="w-4 h-4" /> : <Package className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setExpandedMaterial(isExpanded ? null : material.id)}
                className="p-2 bg-zinc-700 hover:bg-zinc-600 text-gray-300 rounded-lg transition-all"
              >
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Performance Index */}
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-blue-400" />
              <span className="text-xs text-gray-400">Wskaźnik wydajności</span>
            </div>
            <div className="w-full bg-zinc-700 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-blue-500 to-blue-400 h-2 rounded-full"
                style={{ width: `${performanceIndex}%` }}
              />
            </div>
            <span className="text-xs text-gray-500">{performanceIndex.toFixed(0)}%</span>
          </div>

          {/* Kluczowe właściwości */}
          <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
            <div className="flex items-center gap-1">
              <Shield className="w-3 h-3 text-gray-500" />
              <span className="text-gray-400">Wytrzymałość:</span>
              <span className={`font-medium ${getPropertyColor(material.mechanical.tensileStrength, 30, 100)}`}>
                {material.mechanical.tensileStrength} MPa
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Zap className="w-3 h-3 text-gray-500" />
              <span className="text-gray-400">Udarność:</span>
              <span className="font-medium text-gray-200">
                {typeof material.mechanical.impactStrength === 'number' 
                  ? `${material.mechanical.impactStrength} kJ/m²`
                  : material.mechanical.impactStrength}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Thermometer className="w-3 h-3 text-gray-500" />
              <span className="text-gray-400">Max temp:</span>
              <span className={`font-medium ${getPropertyColor(material.thermal.maxServiceTemp, 60, 260)}`}>
                {material.thermal.maxServiceTemp}°C
              </span>
            </div>
            <div className="flex items-center gap-1">
              <DollarSign className="w-3 h-3 text-gray-500" />
              <span className="text-gray-400">Cena:</span>
              <span className={`font-medium ${getPropertyColor(100 - material.price, 0, 100)}`}>
                {material.price} PLN/kg
              </span>
            </div>
          </div>

          {/* Właściwości optyczne */}
          {material.optical.transparency > 0 && (
            <div className="flex items-center gap-2 mb-3 px-2 py-1 bg-blue-900/30 border border-blue-800 rounded">
              <Eye className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-blue-300">
                Przezroczystość: {material.optical.transparency}%
              </span>
            </div>
          )}

          {/* Recykling */}
          <div className="flex items-center gap-2 mb-3">
            <Leaf className={`w-4 h-4 ${material.sustainability.recyclable ? 'text-green-400' : 'text-gray-600'}`} />
            <span className="text-sm text-gray-400">
              {material.sustainability.recyclable 
                ? `Recykling: Kod ${material.sustainability.recyclingCode || '7'}` 
                : 'Nie podlega recyklingowi'}
            </span>
          </div>

          {/* Zastosowania */}
          <div className="mb-3">
            <p className="text-xs text-gray-500 mb-1">Główne zastosowania:</p>
            <div className="flex flex-wrap gap-1">
              {material.applications.slice(0, 3).map((app, i) => (
                <span key={i} className="px-2 py-1 bg-zinc-700 text-xs text-gray-300 rounded">
                  {app}
                </span>
              ))}
              {material.applications.length > 3 && (
                <span className="px-2 py-1 bg-zinc-600 text-xs text-gray-300 rounded">
                  +{material.applications.length - 3}
                </span>
              )}
            </div>
          </div>

          {/* Rozwinięte szczegóły */}
          {isExpanded && (
            <div className="mt-4 pt-4 border-t border-zinc-700 space-y-4">
              {/* Właściwości mechaniczne */}
              <div>
                <h4 className="font-semibold text-gray-200 mb-2 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-blue-400" />
                  Właściwości mechaniczne
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-gray-400">Moduł Younga: <span className="font-medium text-gray-200">{material.mechanical.tensileModulus} GPa</span></div>
                  <div className="text-gray-400">Wytrzym. na zginanie: <span className="font-medium text-gray-200">{material.mechanical.flexuralStrength} MPa</span></div>
                  <div className="text-gray-400">Wydłużenie: <span className="font-medium text-gray-200">{material.mechanical.elongationAtBreak}%</span></div>
                  <div className="text-gray-400">Twardość: <span className="font-medium text-gray-200">{material.mechanical.hardness}</span></div>
                </div>
              </div>

              {/* Właściwości termiczne */}
              <div>
                <h4 className="font-semibold text-gray-200 mb-2 flex items-center gap-2">
                  <Thermometer className="w-4 h-4 text-red-400" />
                  Właściwości termiczne
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-gray-400">Tg: <span className="font-medium text-gray-200">{material.thermal.glassTransition}°C</span></div>
                  <div className="text-gray-400">Temp. topnienia: <span className="font-medium text-gray-200">{material.thermal.meltingPoint}°C</span></div>
                  <div className="text-gray-400">HDT: <span className="font-medium text-gray-200">{material.thermal.heatDeflection}°C</span></div>
                  <div className="text-gray-400">Palność: <span className="font-medium text-gray-200">{material.thermal.flammability}</span></div>
                </div>
              </div>

              {/* Odporność chemiczna */}
              <div>
                <h4 className="font-semibold text-gray-200 mb-2 flex items-center gap-2">
                  <TestTube className="w-4 h-4 text-purple-400" />
                  Odporność chemiczna
                </h4>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(material.chemical.chemicalResistance).map(([chem, level]) => (
                    <span key={chem} className={`px-2 py-1 rounded text-xs ${getResistanceColor(level)}`}>
                      {chem}: {level}
                    </span>
                  ))}
                </div>
              </div>

              {/* Zalety i wady */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-green-400 mb-2">Zalety</h4>
                  <ul className="space-y-1">
                    {material.advantages.map((adv, i) => (
                      <li key={i} className="flex items-start gap-1">
                        <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-gray-300">{adv}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-red-400 mb-2">Wady</h4>
                  <ul className="space-y-1">
                    {material.disadvantages.map((dis, i) => (
                      <li key={i} className="flex items-start gap-1">
                        <XCircle className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-gray-300">{dis}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Przetwórstwo */}
              <div>
                <h4 className="font-semibold text-gray-200 mb-2">Parametry przetwórstwa</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-gray-400">Temp. wtrysku: <span className="font-medium text-gray-200">{material.processing.moldingTemp.min}-{material.processing.moldingTemp.max}°C</span></div>
                  <div className="text-gray-400">Temp. formy: <span className="font-medium text-gray-200">{material.processing.moldTemp.min}-{material.processing.moldTemp.max}°C</span></div>
                  <div className="text-gray-400">Suszenie: <span className="font-medium text-gray-200">{material.processing.dryingTemp}°C / {material.processing.dryingTime}h</span></div>
                  <div className="text-gray-400">Skurcz: <span className="font-medium text-gray-200">{material.processing.shrinkage}%</span></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-zinc-900 text-white">
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-zinc-800 rounded-xl shadow-2xl p-6 border border-zinc-700">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
              <Database className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Baza Wiedzy o Materiałach</h1>
              <p className="text-gray-400">Kompleksowa baza danych z rzeczywistymi parametrami technicznymi</p>
            </div>
          </div>

          {/* Toolbar */}
          <div className="flex flex-wrap gap-4 mb-6">
            {/* Search */}
            <div className="flex-1 min-w-[300px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Szukaj materiału, nazwy handlowej lub zastosowania..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
                />
              </div>
            </div>

            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Wszystkie kategorie</option>
              <option value="thermoplastic">Termoplasty</option>
              <option value="thermoset">Duroplasty</option>
              <option value="composite">Kompozyty</option>
              <option value="metal">Metale</option>
              <option value="glass">Szkło</option>
            </select>

            {/* View Mode */}
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-4 py-2 rounded-lg transition-all ${
                  viewMode === 'grid' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-zinc-700 hover:bg-zinc-600 text-gray-300'
                }`}
              >
                <Layers className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-4 py-2 rounded-lg transition-all ${
                  viewMode === 'table' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-zinc-700 hover:bg-zinc-600 text-gray-300'
                }`}
              >
                <BarChart3 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('compare')}
                className={`px-4 py-2 rounded-lg transition-all ${
                  viewMode === 'compare' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-zinc-700 hover:bg-zinc-600 text-gray-300'
                }`}
              >
                Porównaj ({selectedMaterials.length})
              </button>
            </div>

            {/* AI Assistant */}
            <button
              onClick={() => setShowAiRecommendations(!showAiRecommendations)}
              className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 ${
                showAiRecommendations 
                  ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white' 
                  : 'bg-zinc-700 hover:bg-zinc-600 text-gray-300'
              }`}
            >
              <Lightbulb className="w-5 h-5" />
              AI Doradca
            </button>
          </div>

          {/* AI Requirements Panel */}
          {showAiRecommendations && (
            <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-700/50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-purple-400" />
                Inteligentny dobór materiału - określ wymagania:
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm text-gray-400">Min. wytrzymałość (MPa)</label>
                  <input
                    type="number"
                    value={aiRequirements.minTensileStrength}
                    onChange={(e) => setAiRequirements(prev => ({...prev, minTensileStrength: Number(e.target.value)}))}
                    className="w-full px-2 py-1 bg-zinc-700 border border-zinc-600 text-white rounded"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400">Min. udarność (kJ/m²)</label>
                  <input
                    type="number"
                    value={aiRequirements.minImpactStrength}
                    onChange={(e) => setAiRequirements(prev => ({...prev, minImpactStrength: Number(e.target.value)}))}
                    className="w-full px-2 py-1 bg-zinc-700 border border-zinc-600 text-white rounded"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400">Max. cena (PLN/kg)</label>
                  <input
                    type="number"
                    value={aiRequirements.maxPrice}
                    onChange={(e) => setAiRequirements(prev => ({...prev, maxPrice: Number(e.target.value)}))}
                    className="w-full px-2 py-1 bg-zinc-700 border border-zinc-600 text-white rounded"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400">Temp. pracy (°C)</label>
                  <div className="flex gap-1">
                    <input
                      type="number"
                      value={aiRequirements.minServiceTemp}
                      onChange={(e) => setAiRequirements(prev => ({...prev, minServiceTemp: Number(e.target.value)}))}
                      className="w-1/2 px-2 py-1 bg-zinc-700 border border-zinc-600 text-white rounded"
                      placeholder="Min"
                    />
                    <input
                      type="number"
                      value={aiRequirements.maxServiceTemp}
                      onChange={(e) => setAiRequirements(prev => ({...prev, maxServiceTemp: Number(e.target.value)}))}
                      className="w-1/2 px-2 py-1 bg-zinc-700 border border-zinc-600 text-white rounded"
                      placeholder="Max"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={aiRequirements.needsTransparency}
                    onChange={(e) => setAiRequirements(prev => ({...prev, needsTransparency: e.target.checked}))}
                    className="rounded bg-zinc-700 border-zinc-600"
                  />
                  <label className="text-sm text-gray-400">Przezroczystość</label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={aiRequirements.outdoor}
                    onChange={(e) => setAiRequirements(prev => ({...prev, outdoor: e.target.checked}))}
                    className="rounded bg-zinc-700 border-zinc-600"
                  />
                  <label className="text-sm text-gray-400">Zastosowanie zewnętrzne</label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={aiRequirements.foodContact}
                    onChange={(e) => setAiRequirements(prev => ({...prev, foodContact: e.target.checked}))}
                    className="rounded bg-zinc-700 border-zinc-600"
                  />
                  <label className="text-sm text-gray-400">Kontakt z żywnością</label>
                </div>
              </div>
              
              {/* AI Recommendations */}
              {aiRecommendedMaterials.length > 0 && (
                <div className="mt-4 p-3 bg-zinc-800 rounded-lg border border-zinc-700">
                  <p className="text-sm font-semibold text-purple-400 mb-2">
                    🎯 Rekomendowane materiały (ranking AI):
                  </p>
                  <div className="space-y-2">
                    {aiRecommendedMaterials.slice(0, 3).map((material, index) => (
                      <div key={material.id} className="flex items-center gap-3 p-2 bg-purple-900/20 border border-purple-700/30 rounded">
                        <span className="flex items-center justify-center w-8 h-8 bg-purple-500 text-white rounded-full font-bold">
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <p className="font-semibold text-white">{material.name}</p>
                          <p className="text-xs text-gray-400">
                            Wytrzymałość: {material.mechanical.tensileStrength} MPa | 
                            Cena: {material.price} PLN/kg | 
                            Max temp: {material.thermal.maxServiceTemp}°C
                          </p>
                        </div>
                        <button
                          onClick={() => toggleMaterialSelection(material.id)}
                          className="px-3 py-1 bg-purple-500 text-white rounded text-sm hover:bg-purple-600"
                        >
                          Wybierz
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Materials Grid/Table/Comparison */}
          {viewMode === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredMaterials.map(material => (
                <MaterialCard key={material.id} material={material} />
              ))}
            </div>
          )}

          {viewMode === 'table' && (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-zinc-800 border border-zinc-700 rounded-lg">
                <thead className="bg-zinc-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Materiał</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Gęstość</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Wytrzymałość</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Moduł</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Udarność</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Max Temp</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Cena</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Akcje</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-700">
                  {filteredMaterials.map(material => (
                    <tr key={material.id} className="hover:bg-zinc-700/50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-white">{material.name}</p>
                          <p className="text-xs text-gray-500">{material.tradenames[0]}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">{material.density} g/cm³</td>
                      <td className="px-4 py-3 text-sm text-gray-300">{material.mechanical.tensileStrength} MPa</td>
                      <td className="px-4 py-3 text-sm text-gray-300">{material.mechanical.tensileModulus} GPa</td>
                      <td className="px-4 py-3 text-sm text-gray-300">
                        {typeof material.mechanical.impactStrength === 'number' 
                          ? `${material.mechanical.impactStrength} kJ/m²`
                          : material.mechanical.impactStrength}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">{material.thermal.maxServiceTemp}°C</td>
                      <td className="px-4 py-3 text-sm text-gray-300">{material.price} PLN/kg</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleMaterialSelection(material.id)}
                          className={`px-2 py-1 rounded text-xs ${
                            selectedMaterials.includes(material.id)
                              ? 'bg-blue-500 text-white'
                              : 'bg-zinc-700 hover:bg-zinc-600 text-gray-300'
                          }`}
                        >
                          {selectedMaterials.includes(material.id) ? 'Wybrano' : 'Wybierz'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {viewMode === 'compare' && selectedMaterials.length > 0 && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-white">
                  Porównanie {selectedMaterials.length} materiałów
                </h3>
                <button
                  onClick={downloadComparisonReport}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Pobierz raport
                </button>
              </div>
              
              {/* Comparison content */}
              <div className="space-y-6">
                {/* Selected materials overview */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {selectedMaterials.map(id => {
                    const material = MATERIALS_DATABASE.find(m => m.id === id);
                    if (!material) return null;
                    return (
                      <div key={id} className="bg-zinc-700 rounded-lg p-4 border border-zinc-600">
                        <h4 className="font-semibold text-white">{material.name}</h4>
                        <p className="text-sm text-gray-400">{material.tradenames[0]}</p>
                        <button
                          onClick={() => toggleMaterialSelection(id)}
                          className="mt-2 text-xs text-red-400 hover:text-red-300"
                        >
                          Usuń z porównania
                        </button>
                      </div>
                    );
                  })}
                </div>
                
                {/* Detailed comparison tables */}
                <div className="bg-zinc-700/50 rounded-lg p-4 border border-zinc-600">
                  <h4 className="font-semibold text-white mb-3">Właściwości mechaniczne</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="border-b border-zinc-600">
                          <th className="text-left text-xs text-gray-400 p-2">Parametr</th>
                          {selectedMaterials.map(id => {
                            const mat = MATERIALS_DATABASE.find(m => m.id === id);
                            return <th key={id} className="text-left text-xs text-gray-300 p-2">{mat?.name}</th>;
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-zinc-700">
                          <td className="text-sm text-gray-400 p-2">Wytrzymałość [MPa]</td>
                          {selectedMaterials.map(id => {
                            const mat = MATERIALS_DATABASE.find(m => m.id === id);
                            return <td key={id} className="text-sm text-white p-2">{mat?.mechanical.tensileStrength}</td>;
                          })}
                        </tr>
                        <tr className="border-b border-zinc-700">
                          <td className="text-sm text-gray-400 p-2">Moduł [GPa]</td>
                          {selectedMaterials.map(id => {
                            const mat = MATERIALS_DATABASE.find(m => m.id === id);
                            return <td key={id} className="text-sm text-white p-2">{mat?.mechanical.tensileModulus}</td>;
                          })}
                        </tr>
                        <tr className="border-b border-zinc-700">
                          <td className="text-sm text-gray-400 p-2">Udarność</td>
                          {selectedMaterials.map(id => {
                            const mat = MATERIALS_DATABASE.find(m => m.id === id);
                            return <td key={id} className="text-sm text-white p-2">{mat?.mechanical.impactStrength}</td>;
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {viewMode === 'compare' && selectedMaterials.length === 0 && (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Wybierz materiały do porównania</p>
              <p className="text-sm text-gray-500 mt-2">Możesz wybrać maksymalnie 4 materiały</p>
            </div>
          )}

          {/* Stats */}
          <div className="mt-8 pt-6 border-t border-zinc-700">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-blue-400">{MATERIALS_DATABASE.length}</p>
                <p className="text-sm text-gray-400">Materiałów w bazie</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-400">
                  {MATERIALS_DATABASE.filter(m => m.sustainability.recyclable).length}
                </p>
                <p className="text-sm text-gray-400">Podlega recyklingowi</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-400">
                  {MATERIALS_DATABASE.filter(m => m.optical.transparency > 80).length}
                </p>
                <p className="text-sm text-gray-400">Przezroczyste</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-400">
                  {MATERIALS_DATABASE.filter(m => m.thermal.maxServiceTemp > 100).length}
                </p>
                <p className="text-sm text-gray-400">Wysokotemperaturowe</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialsDatabaseComponent;